import intersection from 'lodash/intersection.js';
import {jwtDecode} from 'jwt-decode';

/**
 * Role levels are the fundamental value attached to users that define their admin permissions.
 * Here we create an object that defines all role levels, which are accessible by
 * their human readable property names.
 */
export const roleLevels = {
  planet_admin: 2000,
  planet_user: 1999,
  org_admin: 1000,
  org_user: 100,
};

export const roles = {
  planet_admin: 'planet_admin',
  planet_user: 'planet_user',
  org_admin: 'org_admin',
  org_user: 'org_user',
};

/* We could do this programmatically but an explicit mapping seems more maintainable */
export const roleStrings = {
  planet_admin: 'Planet admin',
  planet_user: 'Planet user',
  org_admin: 'Org admin',
  org_user: 'Org user',
};

export const translateRoleLevel = numericRoleLevel => {
  const key = Object.keys(roleLevels).find(
    level => roleLevels[level] === numericRoleLevel
  );
  return roleStrings[key] ?? 'Unknown';
};

/**
 * DEV NOTE: user objects from v1 spell out the 'role' property's value
 * while v0 'role' property is abbreviated with underscores. The user auth token
 * is based on v0, therefore the below 'roles', 'roleNames' variables
 * ex) 'role' property: v1 = "Organization Admin" v0 = "org_admin"
 */
export const v1RoleNamesTov0RoleNames = {
  'Organization Admin': roles.org_admin,
  'Organization User': roles.org_user,
  'Planet Admin': roles.planet_admin,
  'Planet User': roles.planet_user,
};

/**
 * "Role Names" are the display version of the role levels we want to present to the user
 */
export const roleNames = {
  [roleLevels.planet_admin]: 'Admin Planeteer',
  [roleLevels.planet_user]: 'Planeteer',
  [roleLevels.org_admin]: 'Admin',
  [roleLevels.org_user]: 'User',
};

// Determines what kind of user role change operation we want to perform
export const roleChangeInfo = user => {
  return user.role_level >= roleLevels.org_user
    ? user.role_level < roleLevels.org_admin
      ? {
          // Regular user to admin user
          title: `Promote this user to a ${roleNames[roleLevels.org_admin]}`,
          buttonText: 'Grant Admin Role',
          role: roles.org_admin,
          level: roleLevels.org_admin,
        }
      : {
          // Admin user to regular user
          title: `Demote this user to a ${roleNames[roleLevels.org_user]}`,
          buttonText: 'Revoke Admin Role',
          role: roles.org_user,
          level: roleLevels.org_user,
        }
    : {
        // low level user (no role set) to regular user
        title: `Promote this user to a ${roleNames[roleLevels.org_user]}`,
        buttonText: `Grant ${roleNames[roleLevels.org_user]} Role`,
        role: roles.org_user,
        level: roleLevels.org_user,
      };
};

export const stateInfo = user => {
  const newState = user.state === 'disabled' ? 'active' : 'disabled';
  return {
    newState,
    stateText: newState === 'disabled' ? 'Disable' : 'Enable',
  };
};

export const isPlaneteer = user => {
  const validDomains = ['planet.com', 'federal.planet.com'];
  return validDomains.indexOf(user?.email?.split('@').pop()) >= 0;
};

/**
 * @param {import('@apps-common/auth-react').AuthState} auth The auth state.
 * @param {Array<string>} roles The roles to check.
 * @return {boolean} The user has one of the role levels.
 */
export function hasRoleLevel(auth, roles) {
  if (!Array.isArray(roles)) {
    // eslint-disable-next-line no-console
    console.error('roles should be an array');
    return false;
  }
  const intersect = intersection(Object.values(roleLevels), roles);
  /** Make sure the roles passed in are valid */
  if (intersect.length > 0) {
    return intersect.includes(auth.user?.claims.role_level);
  }
  return false;
}

/**
 * @param {import('@apps-common/auth-react').AuthState} auth The auth state.
 * @param {string} role The role to check.
 * @return {boolean} The user has the provided role or higher.
 */
export function meetsRole(auth, role) {
  const claims = auth.user?.claims || {};
  /** Make sure the role passed in is valid */
  if (roleLevels[role]) {
    /** For planet roles, do another check on email domains */
    if (role === roles.planet_user || role === roles.planet_admin) {
      return claims.role_level >= roleLevels[role] && isPlaneteer(claims);
    }
    /** Make sure the user's role level is greater than the value of the passed in role */
    return claims.role_level >= roleLevels[role];
  }
  return false;
}

/**
 * @param {import('@apps-common/auth-react').AuthState} auth The auth state.
 * @param {Object} user The user object.
 * @return {boolean} The authenticated user can change the other user's status.
 */
export function canChangeUserStatus(auth, user) {
  const claims = auth.user?.claims || {};
  // Determines if the viewer is the display user
  const isSameUser = claims.user_id === user.id;
  const isPlanetUser = claims.role_level === roleLevels.planet_user;

  if (isSameUser || isPlanetUser) {
    return false;
  }

  if (claims.role_level === roleLevels.planet_admin) {
    return true;
  }

  if (claims.role_level === roleLevels.org_admin) {
    // TODO: We should also be sure the org admin belongs to the same org as
    // (or a parent org of) the user's org
    return claims.role_level > user.role_level;
  }

  return false;
}

/**
 * @param {import('@apps-common/auth-react').AuthState} auth The auth state.
 * @return {string} The api_key claim (or '' if no user).
 */
export function getApiKey(auth) {
  if (!auth.user) {
    return '';
  }
  return jwtDecode(auth.user.accessToken).api_key;
}
